import React, { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import {
	isSupplierPayingCustomer,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import WorkOrderTable from '../WorkOrderTable';
import { ASSIGNED_FILTERS } from '../WorkOrderTable/config/assigned';
import { ACTIVE_WORK_ORDER_TAB_FILTERS } from '../WorkOrderTable/config/tabFilters/tabFilters';
import { SUPPLIER_TECH } from '../roles/roles';
import {
	INTERNAL_SERVICE_TEAM_WO_FILTERS_ORDER,
	PAYING_SUPPLIER_WO_FILTER_ORDER,
	THIRD_PARTY_SUPPLIER_WO_FILTERS_ORDER,
} from '../../utils/WorkOrderUtils';
import { SUBCONTRACTED_FILTERS } from '../WorkOrderTable/config/subcontracted';
import {
	isWorkOrderLabelsTrackEnabled,
	isWorkOrderRemindersEnabled,
} from '../../data/workOrderStarLabels';
import { TrackedWorkOrderTableEmptyState } from '../WorkOrderTable/emptyState/WorkOrderTableEmptyState';
import TrackedReminderGroups, {
	TRACKED_GROUP_PRE_APPLIED_FILTERS,
	TRACKED_PRE_APPLIED_FILTERS,
} from '../work_orders_starred_index_page/TrackedReminderGroups';

// `starredByCurrentUser: true` is a pre-applied filter, so it is sent to the API
// (returning only the current user's starred work orders) AND written to the URL
// via handleQueryParams — so the starred view persists across refreshes. This is the
// supplier counterpart of WorkOrdersStarredIndexPage; the filter UI mirrors the supplier
// active index page so the advanced filters match the supplier user type.
function SupplierWorkOrdersStarredIndexPage({ currentUser }) {
	const isInternalTech = useMemo(
		() => nullSafeGetOrElse('facility.status', currentUser, '') === 'private',
		[currentUser]
	);

	const advancedFiltersConfig = useMemo(() => {
		const order = isInternalTech
			? INTERNAL_SERVICE_TEAM_WO_FILTERS_ORDER
			: isSupplierPayingCustomer(currentUser)
			? PAYING_SUPPLIER_WO_FILTER_ORDER
			: THIRD_PARTY_SUPPLIER_WO_FILTERS_ORDER;
		return order.filter((filterName) => filterName !== FILTER_CONFIG_NAMES.STARRED);
	}, [currentUser, isInternalTech]);

	// Mirror the supplier overview pages (Active/All/Archived): when a tech's org has the
	// "default technician view shows only assigned work orders" setting on, the starred
	// view must respect it too — otherwise it lists every work order the tech starred,
	// including ones not assigned to them.
	const showMineAsInitialFilter =
		isInternalTech &&
		nullSafeGetOrElse('roles', currentUser, []).some((role) => role === SUPPLIER_TECH) &&
		nullSafeGetOrElse(
			'facility.privateBuyerCompanySettings.config.workOrder.showOnlyAssignedWorkOrdersInDefaultTechnicianView',
			currentUser,
			false
		);
	const initialFilters = showMineAsInitialFilter
		? {
				assigned: 'me',
				supplierFacilityId: nullSafeGet('facility.id', currentUser),
				fieldTechnicianEmail: nullSafeGet('email', currentUser),
				notSupplierFacilityId: undefined,
		  }
		: {};

	// Gated behind workorder-labels-track; hidden from tabs/pickers when off, so guard the direct
	// route too (this early-returns after all hooks, keeping hook order stable).
	if (!isWorkOrderLabelsTrackEnabled()) {
		return <Redirect to="/supplier/workOrders/overview/active" />;
	}

	// With reminders off, one plain list instead of the Active / Upcoming / Tracked groups.
	const remindersEnabled = isWorkOrderRemindersEnabled();
	return (
		<WorkOrderTable
			handleQueryParams
			preAppliedFilters={
				remindersEnabled ? TRACKED_GROUP_PRE_APPLIED_FILTERS : TRACKED_PRE_APPLIED_FILTERS
			}
			renderEmptyState={TrackedWorkOrderTableEmptyState}
			renderBody={
				remindersEnabled
					? (bodyProps) => <TrackedReminderGroups {...bodyProps} userType={ROLE_TYPES.SUPPLIER} />
					: undefined
			}
			filterConfig={{
				...(!isInternalTech && { subContractedFilters: SUBCONTRACTED_FILTERS(currentUser) }),
				assignedFilters: ASSIGNED_FILTERS(currentUser),
				tabFilters: ACTIVE_WORK_ORDER_TAB_FILTERS,
				filters: advancedFiltersConfig,
			}}
			initialFilters={initialFilters}
		/>
	);
}

export default withCurrentUser(SupplierWorkOrdersStarredIndexPage);
